import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Badge, Button, buttonStyles, Field, Select, Spinner } from '@zt/ui';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useApiMutation } from '../lib/mutation';
import type { BankImportPreview } from '../lib/types';

/**
 * Banka hareketi dosyasıyla toplu tahsilat (F7-23, ADR-114): döküm yüklenir, gelen hareketler bayilerle eşleştirilir;
 * yetkili satırları ve bayileri onaylar, seçilenler havale tahsilatı olarak deftere yazılır.
 */

type Row = BankImportPreview['rows'][number];

/** Güçlü eşleşmeler (sipariş no, bayi kodu, BizimHesap kodu, vergi no) önceden seçili gelir; unvan benzerliği değil. */
const STRONG = new Set(['order', 'code', 'bizimhesap', 'tax']);

export function BankImportPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<BankImportPreview | null>(null);
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  const [dealer, setDealer] = useState<Record<number, string>>({});
  const [idem, setIdem] = useState(() => new IdempotencyKey());

  const upload = useApiMutation(
    (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload<BankImportPreview>('/v1/admin/ledger/bank-imports', fd);
    },
    {
      onSuccess: (r) => {
        setPreview(r);
        setIdem(new IdempotencyKey());
        setDealer(Object.fromEntries(r.rows.filter((x) => x.companyId).map((x) => [x.line, x.companyId!])));
        setPicked(
          Object.fromEntries(
            r.rows.map((x) => [x.line, !x.issue && !!x.companyId && STRONG.has(x.matchBy ?? '')]),
          ),
        );
      },
    },
  );

  const selected = useMemo(
    () => (preview?.rows ?? []).filter((r) => picked[r.line] && !r.issue),
    [preview, picked],
  );
  const missingDealer = selected.some((r) => !dealer[r.line]);
  const totals = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of selected) m[r.currency] = (m[r.currency] ?? 0) + r.amountMinor;
    return m;
  }, [selected]);

  const apply = useApiMutation(
    () =>
      api.post<{ applied: number; bizimhesap: { queued: number; sent: number } }>(
        `/v1/admin/ledger/bank-imports/${preview!.id}/apply`,
        { rows: selected.map((r) => ({ line: r.line, companyId: dealer[r.line]! })) },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['ledger'], ['company-ledger'], ['bizimhesap']],
      success: (r) =>
        r.bizimhesap.queued
          ? t('admin.bankImport.appliedWithBh', { count: r.applied, queued: r.bizimhesap.queued })
          : t('admin.bankImport.applied', { count: r.applied }),
      onSuccess: () => void navigate({ to: '/cari-hesaplar' }),
    },
  );

  const matchLabel = (r: Row) => {
    if (r.issue) return <span className="text-rose">{t(`admin.bankImport.issues.${r.issue}`)}</span>;
    if (r.matchBy === 'order')
      return <Badge tone="moss">{t('admin.bankImport.match.order', { number: r.orderNumber ?? '' })}</Badge>;
    if (r.matchBy === 'name') return <Badge tone="amber">{t('admin.bankImport.match.name')}</Badge>;
    if (r.matchBy) return <Badge tone="moss">{t(`admin.bankImport.match.${r.matchBy}`)}</Badge>;
    if (r.candidates.length) return <Badge tone="amber">{t('admin.bankImport.match.ambiguous')}</Badge>;
    return <Badge tone="neutral">{t('admin.bankImport.match.none')}</Badge>;
  };

  return (
    <PageFrame
      helpKey="page.ledger_bank_import"
      actions={
        <Link to="/cari-hesaplar" className={buttonStyles({ variant: 'secondary' })}>
          {t('admin.bankImport.back')}
        </Link>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-ink-2">{t('admin.bankImport.columns')}</p>
        <Field label={t('admin.bankImport.file')} help={<HelpTip k="ledger.bank_import_file" />}>
          {(p) => (
            <input
              {...p}
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="block text-sm"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setPreview(null);
                if (f) upload.mutate(f);
              }}
            />
          )}
        </Field>
        {upload.isPending && <Spinner label={t('common.loading')} />}
        {preview && (
          <>
            <p role="status" className="font-medium">
              {t('admin.bankImport.summary', {
                incoming: preview.summary.incoming,
                matched: preview.summary.matched,
                ambiguous: preview.summary.ambiguous,
                unmatched: preview.summary.unmatched,
                blocked: preview.summary.blocked,
                outgoing: preview.summary.outgoing,
              })}
            </p>
            <p className="text-ink-3">
              {t('admin.bankImport.validUntil', { time: formatDate(preview.expiresAt, true) })}
            </p>
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full min-w-[56rem] text-sm">
                <caption className="sr-only">{t('admin.bankImport.tableCaption')}</caption>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="w-10 px-3 py-2">
                      <span className="sr-only">{t('admin.bankImport.pick')}</span>
                    </th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.bankImport.date')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.bankImport.description')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.bankImport.amount')}</th>
                    <th className="px-3 py-2 text-start font-medium">
                      <span className="inline-flex items-center gap-1">
                        {t('admin.bankImport.dealer')}
                        <HelpTip k="ledger.bank_import_dealer" />
                      </span>
                    </th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.bankImport.matchColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const candidateIds = new Set(r.candidates.map((c) => c.id));
                    return (
                      <tr key={r.line} className="border-t border-line align-top">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            className="accent-[var(--zt-pine)]"
                            aria-label={t('admin.bankImport.pickRow', { line: r.line })}
                            disabled={!!r.issue || preview.status !== 'preview'}
                            checked={!!picked[r.line] && !r.issue}
                            onChange={(e) => setPicked((cur) => ({ ...cur, [r.line]: e.target.checked }))}
                          />
                        </td>
                        <td className="px-3 py-2 tabular">{r.date ? formatDate(r.date, false) : '—'}</td>
                        <td className="px-3 py-2">
                          {r.description || '—'}
                          {(r.sender || r.reference) && (
                            <span className="block text-ink-3">
                              {[
                                r.sender,
                                r.reference && t('admin.bankImport.reference', { ref: r.reference }),
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          )}
                          <span className="block text-xs text-ink-3">
                            {t('admin.bankImport.line', { line: r.line })}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-end tabular">
                          {r.issue === 'amount_invalid' ? '—' : money(r.amountMinor, r.currency)}
                        </td>
                        <td className="px-3 py-2">
                          {r.issue ? (
                            (r.companyName ?? '—')
                          ) : (
                            <Select
                              aria-label={t('admin.bankImport.dealerFor', { line: r.line })}
                              value={dealer[r.line] ?? ''}
                              disabled={preview.status !== 'preview'}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDealer((cur) => ({ ...cur, [r.line]: v }));
                                setPicked((cur) => ({ ...cur, [r.line]: !!v }));
                              }}
                            >
                              <option value="">{t('admin.bankImport.chooseDealer')}</option>
                              {r.candidates.length > 0 && (
                                <optgroup label={t('admin.bankImport.candidates')}>
                                  {r.candidates.map((c) => (
                                    <option key={c.id} value={c.id}>
                                      {c.legalName}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              <optgroup label={t('admin.bankImport.allDealers')}>
                                {preview.dealers
                                  .filter((d) => !candidateIds.has(d.id))
                                  .map((d) => (
                                    <option key={d.id} value={d.id}>
                                      {d.externalCode ? `${d.legalName} (${d.externalCode})` : d.legalName}
                                    </option>
                                  ))}
                              </optgroup>
                            </Select>
                          )}
                        </td>
                        <td className="px-3 py-2">{matchLabel(r)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {preview.status === 'preview' ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <Button
                    variant="primary"
                    disabled={!selected.length || missingDealer}
                    loading={apply.isPending}
                    onClick={() => apply.mutate()}
                  >
                    {t('admin.bankImport.apply', {
                      count: selected.length,
                      total:
                        Object.entries(totals)
                          .map(([cur, v]) => money(v, cur))
                          .join(' + ') || '—',
                    })}
                  </Button>
                  <HelpTip k="ledger.bank_import_apply" />
                </span>
                {missingDealer && <span className="text-rose">{t('admin.bankImport.missingDealer')}</span>}
              </div>
            ) : (
              <p role="status">{t('admin.bankImport.alreadyApplied')}</p>
            )}
          </>
        )}
      </div>
    </PageFrame>
  );
}
